// @ts-strict-ignore
import {
  actionHasRules, isActionOfType,
} from 'flow/service/actions';
import type { PredicatedProcessor } from '../../lib/predicatedProcessor';
import { createPredicatedProcessor } from '../../lib/predicatedProcessor';
import type {
  ActionAddedChange,
  ActionMLAAddedChange,
  ActionRemovedChange,
  Change,
  ChangelogInputBundle,
  InputActionMetadataModifiedChange,
  MatrixModelActionMetadataModifiedChange,
  OutcomeActionMetadataModifiedChange,
  OutputActionExpressionModifiedChange,
  OutputActionMetadataModifiedChange,
} from '../../types';
import {
  Operation,
} from '../../types';


import type {
  TSuiteBuffer, TSuiteBufferEntry, RuleChangeExistence,
} from './types';
import {
  getRelevantVersionForOp,
  getRelevantWorkflowPropsForOp,
  getUniqueActionPublicIds,
  synthesizeSingleOperationForChanges,
} from '../helpers';
import {
  prependWithIneligibleChange,
  getChangeExistenceForRuleChangesByActionId,
} from './helpers';
import { INELIGIBILITY_MAP } from './constants';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import Predicates from './predicates';
import type createTemplates from './templates';


export function createChangeProcessors(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  entriesBuffer: TSuiteBuffer;
  templates: ReturnType<typeof createTemplates>;
}): Record<string, PredicatedProcessor<Change>> {
  const {
    entriesBuffer,
    A: {
      props: propsA,
      version: versionA,
    },
    B: {
      props: propsB,
      version: versionB,
    },
    templates,
  } = args;

  // explicitly typed function for better generic type safety
  const createChangeProcessor = createPredicatedProcessor<Change>;
  // curried function to not have to pass `templates` down every time
  const buildIneligibleChangeEntry = (
    entries: TSuiteBufferEntry | TSuiteBufferEntry[],
  ) => prependWithIneligibleChange({ entries, templates });

  return {
    actionSectionTitle: createChangeProcessor({
      predicates: [
        // grab everything so we can build an action section
        Predicates.everything,
      ],
      process: (
        allActionChanges => {
          const changesByActionId = allActionChanges.reduce((changesRecord, change) => {
            const { actionPublicId } = change;
            if (!Object.hasOwn(changesRecord, actionPublicId)) {
              changesRecord[actionPublicId] = [];
            }
            changesRecord[actionPublicId].push(change);
            return changesRecord;
          }, {} as Record<string, Change[]>);


          const actionSectionTitleEntries: TSuiteBufferEntry[] = Object
            .entries(changesByActionId)
            .flatMap(([actionPublicId, changesForAction]) => {
              const primaryOperationInChanges = synthesizeSingleOperationForChanges(
                changesForAction,
              );

              const versionToReadFrom = getRelevantVersionForOp({
                operation: primaryOperationInChanges,
                versions: {
                  versionA,
                  versionB,
                },
              });

              const action = versionToReadFrom.actions[actionPublicId];
              return {
                actionPublicId,
                line: templates.actionSectionTitle({
                  action,
                  operation: primaryOperationInChanges,
                }),
              };
            });

          entriesBuffer.append(actionSectionTitleEntries);
        }
      ),
    }),
    actionAddedOrRemoved: createChangeProcessor({
      operator: 'some',
      predicates: [
        Predicates.actionAdded,
        Predicates.actionRemoved,
        Predicates.actionMLAAdded,
      ],
      process: (
        addedActionChanges: ActionAddedChange[],
        removedActionChanges: ActionRemovedChange[],
        addedActionMLAChanges: ActionMLAAddedChange[],
      ) => {
        const ruleChangeExistenceByActionId = getChangeExistenceForRuleChangesByActionId(
          addedActionMLAChanges,
        );

        const entries: TSuiteBufferEntry[] = [
          ...addedActionChanges,
          ...removedActionChanges,
        ].flatMap(change => {
          const { actionPublicId, value: { prev: prevAction, next: nextAction }, op } = change;

          const versionToReadFrom = getRelevantVersionForOp({
            operation: op,
            versions: { versionA, versionB },
          });
          const wfPropsToReadFrom = getRelevantWorkflowPropsForOp({
            operation: op,
            workflowProps: {
              propsA,
              propsB,
            },
          });

          const action = op === Operation.Add ? nextAction : prevAction;


          let line: TSuiteBufferEntry['line'];
          if (actionHasRules(action)) {
            line = templates.actionRulesAsString({
              deps: {
                action,
                actions: versionToReadFrom.actions,
                reasonCodeGroups: wfPropsToReadFrom.reasonCodeGroups,
                services: wfPropsToReadFrom.services,
                thresholds: versionToReadFrom.thresholds,
              },
            });
          } else {
            switch (action.type) {
              case ActionType.SERVICE:
                line = templates.serviceAlwaysRuns({ serviceAction: action });
                break;
              case ActionType.INPUT:
                line = templates.inputActionMetadata({ deps: { inputAction: action } });
                break;
              case ActionType.OUTPUT:
                line = templates.outputActionMetadata({
                  deps: {
                    outputAction: action,
                    actions: versionToReadFrom.actions,
                    dataRoutes: versionToReadFrom.dataRoutes,
                  },
                });
                break;
              case ActionType.OUTCOME:
                line = templates.defaultOutcome({ outcomeAction: action });
                break;
              case ActionType.IMPORTED_MATRIX_MODEL:
              case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
              case ActionType.OUTCOME_REASON:
              case ActionType.MATRIX_MODEL:
                line = templates.actionName({ action });
                break;

              default:
                return [];
            }
          }

          const valueEntry: TSuiteBufferEntry = {
            actionPublicId,
            line,
          };

          const ineligibleTypesForOp = INELIGIBILITY_MAP[op];
          const isIneligible = isActionOfType(nextAction, ineligibleTypesForOp)
            /**
             * Adding an Action with an MLA is an ineligible change. Removals are fine,
             * and Modifications are handled by each action type processor below.
             */
            || ruleChangeExistenceByActionId[actionPublicId]?.mlaOperations[Operation.Add];

          return isIneligible
            ? buildIneligibleChangeEntry(valueEntry)
            : [valueEntry];
        });

        entriesBuffer.append(entries);
      },
    }),
    tagActionModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        Predicates.addOrModifyRuleChangesForTags,
        Predicates.tagActionAddedOrRemoved,
      ],
      process: (
        allRuleChangesForTagActionChanges: Change[],
        tagActionAddedOrRemovedChanges: (ActionAddedChange | ActionRemovedChange)[],
      ) => {
        /**
         * these xActionModified processors should only handle modification changes on
         * **existing actions**, including rule changes. however, they can also unintentionally
         * run when an action with a rule is added or removed, and that introduces collateral
         * changes into the processor, which should **not** be turned into entries. to solve this,
         * we gather the IDs for the added or removed actions and exclude them from processing.
         * this is necessary due to the nature of the testing suite formatter, which requires a
         * single pair of from/to entries for any rule change, and building them in the same
         * processor (instead of from multiple processors) simplifies the mental model by a lot.
         */
        const actionIdsFromCollateralChanges = getUniqueActionPublicIds(
          tagActionAddedOrRemovedChanges,
        );

        const ruleChangeExistenceByActionId = getChangeExistenceForRuleChangesByActionId(
          allRuleChangesForTagActionChanges,
        );

        const entries: TSuiteBufferEntry[] = Object.entries(ruleChangeExistenceByActionId)
          .filter(([actionPublicId]) => !actionIdsFromCollateralChanges.has(actionPublicId))
          .flatMap(([actionPublicId, changeExistence]) => {
            const prevTagAction = versionA.actions[actionPublicId];
            const nextTagAction = versionB.actions[actionPublicId];

            const fromEntry: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.tagActionMetadata({
                deps: {
                  action: prevTagAction,
                  actions: versionA.actions,
                  reasonCodeGroups: propsA.reasonCodeGroups,
                  services: propsA.services,
                  thresholds: versionA.thresholds,
                },
                format: {
                  direction: 'from',
                },
              }),
            };

            const toEntry: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.tagActionMetadata({
                deps: {
                  action: nextTagAction,
                  actions: versionB.actions,
                  reasonCodeGroups: propsB.reasonCodeGroups,
                  services: propsB.services,
                  thresholds: versionB.thresholds,
                },
                format: {
                  direction: 'to',
                },
              }),
            };

            const result = [
              toEntry,
              fromEntry,
            ];

            const isIneligible = changeExistence.mlaOperations[Operation.Add]
              || changeExistence.mlaOperations[Operation.Modify];

            return isIneligible
              ? buildIneligibleChangeEntry(result)
              : result;
          });

        entriesBuffer.append(entries);
      },
    }),
    outcomeActionModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        Predicates.outcomeMetadataModified,
        Predicates.addOrModifyRuleChangesForOutcomes,
        Predicates.outcomeActionAddedOrRemoved,
      ],
      process: (
        modifiedOutcomeMetadataChanges: OutcomeActionMetadataModifiedChange[],
        allRuleChangesForOutcomeActionChanges: Change[],
        outcomeActionAddedOrRemovedChanges: (ActionAddedChange | ActionRemovedChange)[],
      ) => {
        const actionIdsFromCollateralChanges = getUniqueActionPublicIds(
          outcomeActionAddedOrRemovedChanges,
        );

        const ruleChangeExistenceByActionId = getChangeExistenceForRuleChangesByActionId(
          allRuleChangesForOutcomeActionChanges,
        );

        /**
         * we combine both types of changes and generate an augmented existence object for each action.
         * this is because there could be an outcome that has a metadata change but no rule changes and otherwise
         * they would not be picked up
         */
        const changeExistenceByActionId = [
          ...modifiedOutcomeMetadataChanges,
          ...allRuleChangesForOutcomeActionChanges,
        ].reduce((changesMap, change) => {
          const { actionPublicId, descriptor } = change;

          if (!Object.hasOwn(changesMap, actionPublicId)) {
            const ruleChangeExistenceForAction = ruleChangeExistenceByActionId[actionPublicId];
            changesMap[actionPublicId] = {
              mlaOperations: {
                [Operation.Add]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Add] ?? false
                ),
                [Operation.Remove]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Remove] ?? false
                ),
                [Operation.Modify]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Modify] ?? false
                ),
              },
              rulesModified: ruleChangeExistenceForAction?.rulesModified ?? false,
              outcomeMetadataModified: false,
            };
          }

          if (descriptor === 'action.outcome.metadata') {
            changesMap[actionPublicId].outcomeMetadataModified = true;
          }

          return changesMap;
        }, {} as Record<string, RuleChangeExistence & { outcomeMetadataModified: boolean }>);


        const entries: TSuiteBufferEntry[] = Object.entries(changeExistenceByActionId)
          .filter(([actionPublicId]) => !actionIdsFromCollateralChanges.has(actionPublicId))
          .flatMap(([actionPublicId, changeExistence]) => {
            const prevOutcomeAction = versionA.actions[actionPublicId];
            const nextOutcomeAction = versionB.actions[actionPublicId];

            const toEntry: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.outcomeActionMetadata({
                deps: {
                  actions: versionB.actions,
                  outcomeAction: nextOutcomeAction,
                  reasonCodeGroups: propsB.reasonCodeGroups,
                  services: propsB.services,
                  thresholds: versionB.thresholds,
                },
                format: {
                  direction: 'to',
                },
                modifications: {
                  outcomeMetadataModified: changeExistence.outcomeMetadataModified,
                  rulesModified: changeExistence.rulesModified,
                },
              }),
            };

            const fromEntry: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.outcomeActionMetadata({
                deps: {
                  actions: versionA.actions,
                  outcomeAction: prevOutcomeAction,
                  reasonCodeGroups: propsA.reasonCodeGroups,
                  services: propsA.services,
                  thresholds: versionA.thresholds,
                },
                modifications: {
                  outcomeMetadataModified: changeExistence.outcomeMetadataModified,
                  rulesModified: changeExistence.rulesModified,
                },
                format: {
                  direction: 'from',
                },
              }),
            };

            const result = [
              toEntry,
              fromEntry,
            ];

            const isIneligible = changeExistence.mlaOperations[Operation.Add]
              || changeExistence.mlaOperations[Operation.Modify];

            return isIneligible
              ? buildIneligibleChangeEntry(result)
              : result;
          });


        entriesBuffer.append(entries);
      },
    }),
    serviceActionModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        Predicates.addOrModifyRuleChangesForServices,
        Predicates.serviceActionAddedOrRemoved,
      ],
      process: (
        allRuleChangesForServiceActionChanges: Change[],
        serviceActionAddedOrRemovedChanges: (ActionAddedChange | ActionRemovedChange)[],
      ) => {
        const actionIdsFromCollateralChanges = getUniqueActionPublicIds(
          serviceActionAddedOrRemovedChanges,
        );

        const ruleChangeExistenceByActionId = getChangeExistenceForRuleChangesByActionId(
          allRuleChangesForServiceActionChanges,
        );


        const entries: TSuiteBufferEntry[] = Object.entries(ruleChangeExistenceByActionId)
          .filter(([actionPublicId]) => !actionIdsFromCollateralChanges.has(actionPublicId))
          .flatMap(([actionPublicId, changeExistence]) => {
            const prevServiceAction = versionA.actions[actionPublicId];
            const nextServiceAction = versionB.actions[actionPublicId];

            const fromEntry: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.serviceActionMetadata({
                deps: {
                  actions: versionA.actions,
                  reasonCodeGroups: propsA.reasonCodeGroups,
                  serviceAction: prevServiceAction,
                  /**
                   * this is a rare exception where we use the later props for the "from" line.
                   * this is due to how the credentials_schema object is dynamically populated
                   * only after the user makes a change to Key/Values in a service, so we need
                   * the non-initial props
                   */
                  services: propsB.services,
                  thresholds: versionA.thresholds,
                },
                format: {
                  direction: 'from',
                },
                modifications: {
                  rulesModified: changeExistence.rulesModified,
                },
              }),
            };

            const toEntry: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.serviceActionMetadata({
                deps: {
                  actions: versionB.actions,
                  reasonCodeGroups: propsB.reasonCodeGroups,
                  serviceAction: nextServiceAction,
                  services: propsB.services,
                  thresholds: versionB.thresholds,
                },
                format: {
                  direction: 'to',
                },
                modifications: {
                  rulesModified: changeExistence.rulesModified,
                },
              }),
            };

            const result = [
              toEntry,
              fromEntry,
            ];

            const isIneligible = changeExistence.mlaOperations[Operation.Add]
              || changeExistence.mlaOperations[Operation.Modify];
            return isIneligible
              ? buildIneligibleChangeEntry(result)
              : result;
          });

        entriesBuffer.append(entries);
      },
    }),
    inputAttributeModified: createChangeProcessor({
      predicates: [
        Predicates.inputMetadataModified,
      ],
      process: (
        inputActionMetadataModifiedChanges: InputActionMetadataModifiedChange[],
      ) => {
        const entries: TSuiteBufferEntry[] = inputActionMetadataModifiedChanges.flatMap(change => {
          const { actionPublicId } = change;
          const prevInputAction = versionA.actions[actionPublicId];
          const nextInputAction = versionB.actions[actionPublicId];

          const fromEntry: TSuiteBufferEntry = {
            actionPublicId,
            line: templates.inputActionMetadata({
              deps: {
                inputAction: prevInputAction,
              },
              format: {
                direction: 'from',
              },
            }),
          };

          const toEntry: TSuiteBufferEntry = {
            actionPublicId,
            line: templates.inputActionMetadata({
              deps: {
                inputAction: nextInputAction,
              },
              format: {
                direction: 'to',
              },
            }),
          };

          const result = [toEntry, fromEntry];
          // input modifications are always ineligible
          return buildIneligibleChangeEntry(result);
        });


        entriesBuffer.append(entries);
      },
    }),
    outputAttributeModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        Predicates.outputMetadataModified,
        Predicates.outputExpressionModified,
        Predicates.addOrModifyRuleChangesForOutputs,
        Predicates.outputActionAddedOrRemoved,
      ],
      process: (
        outputActionMetadataModifiedChanges: OutputActionMetadataModifiedChange[],
        outputActionExpressionModifiedChanges: OutputActionExpressionModifiedChange[],
        allRuleChangesForOutputActionChanges: Change[],
        outputActionAddedOrRemovedChanges: (ActionAddedChange | ActionRemovedChange)[],
      ) => {
        const actionIdsFromCollateralChanges = getUniqueActionPublicIds(
          outputActionAddedOrRemovedChanges,
        );

        const ruleChangeExistenceByActionId = getChangeExistenceForRuleChangesByActionId(
          allRuleChangesForOutputActionChanges,
        );

        const changeExistenceByActionId = [
          ...outputActionMetadataModifiedChanges,
          ...outputActionExpressionModifiedChanges,
          ...allRuleChangesForOutputActionChanges,
        ].reduce((changesMap, change) => {
          const { actionPublicId, descriptor } = change;

          if (!Object.hasOwn(changesMap, actionPublicId)) {
            const ruleChangeExistenceForAction = ruleChangeExistenceByActionId[actionPublicId];
            changesMap[actionPublicId] = {
              mlaOperations: {
                [Operation.Add]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Add] ?? false
                ),
                [Operation.Remove]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Remove] ?? false
                ),
                [Operation.Modify]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Modify] ?? false
                ),
              },
              rulesModified: ruleChangeExistenceForAction?.rulesModified ?? false,
              expressionChanged: false,
            };
          }

          if (descriptor === 'action.output.expression') {
            changesMap[actionPublicId].expressionChanged = true;
          }

          return changesMap;
        }, {} as Record<string, RuleChangeExistence & { expressionChanged: boolean }>);


        const entries: TSuiteBufferEntry[] = Object.entries(changeExistenceByActionId)
          .filter(([actionPublicId]) => !actionIdsFromCollateralChanges.has(actionPublicId))
          .flatMap(([actionPublicId, changeExistence]) => {
            const prevOutputAction = versionA.actions[actionPublicId];
            const nextOutputAction = versionB.actions[actionPublicId];

            const fromEntry: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.outputActionMetadata({
                deps: {
                  outputAction: prevOutputAction,
                  actions: versionA.actions,
                  dataRoutes: versionA.dataRoutes,
                },
                format: {
                  direction: 'from',
                },
              }),
            };

            const toEntry: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.outputActionMetadata({
                deps: {
                  outputAction: nextOutputAction,
                  actions: versionB.actions,
                  dataRoutes: versionB.dataRoutes,
                },
                format: {
                  direction: 'to',
                },
              }),
            };

            const result = [toEntry, fromEntry];

            const isIneligible = changeExistence.mlaOperations[Operation.Add]
              || changeExistence.mlaOperations[Operation.Modify];

            return isIneligible
              ? buildIneligibleChangeEntry(result)
              : result;
          });


        entriesBuffer.append(entries);
      },
    }),
    matrixModelMetadataModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        Predicates.matrixMetadataModified,
        Predicates.addOrModifyRuleChangesForMatrices,
        Predicates.matrixActionAddedOrRemoved,
      ],
      process: (
        matrixModelMetadataModifiedChanges: MatrixModelActionMetadataModifiedChange[],
        allRuleChangesForMatrixActions: Change[],
        matrixActionAddedOrRemovedChanges: (ActionAddedChange | ActionRemovedChange)[],
      ) => {
        const actionIdsFromCollateralChanges = getUniqueActionPublicIds(
          matrixActionAddedOrRemovedChanges,
        );

        const ruleChangeExistenceByActionId = getChangeExistenceForRuleChangesByActionId(
          allRuleChangesForMatrixActions,
        );

        // todo - this whole processor needs to be rewritten once requirements are clearer
        const changeExistenceByActionId = [
          ...matrixModelMetadataModifiedChanges,
          ...allRuleChangesForMatrixActions,
        ].reduce((changesMap, change) => {
          const { actionPublicId } = change;

          if (!Object.hasOwn(changesMap, actionPublicId)) {
            const ruleChangeExistenceForAction = ruleChangeExistenceByActionId[actionPublicId];
            changesMap[actionPublicId] = {
              mlaOperations: {
                [Operation.Add]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Add] ?? false
                ),
                [Operation.Remove]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Remove] ?? false
                ),
                [Operation.Modify]: (
                  ruleChangeExistenceForAction?.mlaOperations[Operation.Modify] ?? false
                ),
              },
              rulesModified: ruleChangeExistenceForAction?.rulesModified ?? false,
            };
          }

          return changesMap;
        }, {} as Record<string, RuleChangeExistence>);

        const entries: TSuiteBufferEntry[] = Object.entries(changeExistenceByActionId)
          .filter(([actionPublicId]) => !actionIdsFromCollateralChanges.has(actionPublicId))
          .flatMap(([actionPublicId, changeExistence]) => {
            const result: TSuiteBufferEntry = {
              actionPublicId,
              line: templates.matrixModelActionMetadata(),
            };

            const isIneligible = changeExistence.mlaOperations[Operation.Add]
              || changeExistence.mlaOperations[Operation.Modify];

            return isIneligible
              ? buildIneligibleChangeEntry(result)
              : [result];
          });

        entriesBuffer.append(entries);
      },
    }),
  };
}
